// SalesReport.jsx
import * as React from "react";
import {
  Box,
  Button,
  Container,
  CssBaseline,
  Divider,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  alpha,
  Chip,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Avatar,
  IconButton,
  Tooltip,
  Fade,
  Zoom,
  Slide,
  Badge,
  CircularProgress
} from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import TimelineIcon from "@mui/icons-material/Timeline";
import AssessmentIcon from "@mui/icons-material/Assessment";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import { DataGrid } from "@mui/x-data-grid";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import AppNavbar from "../../../components/AppNavbar";
import Header from "../../../components/Header";
import SideMenu from "../../../components/SideMenu";
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../../dashboard/theme/customizations';
import AppTheme from "../../../shared-theme/AppTheme";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { formatNumberWithCommas } from "../../../utils/generalFunctions";

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

// Metric Card Component
const MetricCard = ({ label, value, icon, color = "#18586b", trend, trendValue }) => (
  <Card
    sx={{
      background: `linear-gradient(135deg, ${alpha(color, 0.1)}, ${alpha(color, 0.05)})`,
      border: `1px solid ${alpha(color, 0.2)}`,
      borderRadius: 3,
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 12px 24px ${alpha(color, 0.15)}`,
      }
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Avatar
          sx={{
            bgcolor: alpha(color, 0.1),
            color: color,
            width: 48,
            height: 48,
            border: `2px solid ${alpha(color, 0.2)}`
          }}
        >
          {icon}
        </Avatar>
        {trend && (
          <Chip
            label={`${trend === 'up' ? '+' : '-'}${trendValue}%`}
            size="small"
            sx={{
              bgcolor: trend === 'up' ? alpha('#4caf50', 0.1) : alpha('#f44336', 0.1),
              color: trend === 'up' ? '#4caf50' : '#f44336',
              fontWeight: 600,
              border: `1px solid ${trend === 'up' ? alpha('#4caf50', 0.2) : alpha('#f44336', 0.2)}`
            }}
          />
        )}
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 700, color: color, mb: 1 }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {label}
      </Typography>
    </CardContent>
  </Card>
);

export default function SalesReport(props) {
  const navigate = useNavigate();

  // ---- Filters (controlled state) ----
  const [branch, setBranch] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [category, setCategory] = React.useState("");
  const [dateFrom, setDateFrom] = React.useState(null);
  const [dateTo, setDateTo] = React.useState(null);

  // ---- Data state ----
  const [rows, setRows] = React.useState([]);
  const [filteredRows, setFilteredRows] = React.useState([]);
  const [isLoadingData, setIsLoadingData] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [showCharts, setShowCharts] = React.useState(true);

  // ---- Extract unique values for filter options ----
  const filterOptions = React.useMemo(() => {
    if (rows.length === 0) {
      return {
        branches: [],
        statuses: [],
        categories: []
      };
    }

    const branches = [...new Set(rows.map(row => row.sucursal_nombre).filter(Boolean))].sort();
    const statuses = [...new Set(rows.map(row => row.estado_pago).filter(Boolean))].sort();
    
    // Extract categories from the categoria_nombre field
    const categories = [...new Set(rows.map(row => row.categoria_nombre).filter(Boolean))].sort();

    return {
      branches,
      statuses,
      categories
    };
  }, [rows]);

  const currency = (n) => {
    if (n === undefined || n === null || isNaN(n)) return 'RD$0';
    return `RD$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };

  // ---- Data Grid Columns ----
  const columns = [   
    {
      field: "sucursal_nombre",
      headerName: "Sucursal",
      minWidth: 150,
    },
    {
      field: "producto",
      headerName: "Producto",
      minWidth: 180,
      renderCell: (params) => (
        <Box>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 500,
              color: '#18586b',
              cursor: 'pointer',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
                color: '#2a7a8a'
              },
              transition: 'all 0.2s ease',
              mt: 2
            }}
            onClick={() => {
              navigate(`/logistica/productos/${params.row.id_producto}`);
            }}
          >
            {params.value || 'Producto no especificado'}
          </Typography>
        </Box>
      )
    },
    {
      field: "categoria_nombre",
      headerName: "Categoría",
      minWidth: 180,
    },
    {
      field: "cantidad_vendida",
      headerName: "Cantidad",
      minWidth: 120,
      type: 'number',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500,mt:1.5 }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: "precio_unitario_vendido",
      headerName: "Precio Unit.",
      minWidth: 170,
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500,mt:1.5,textAlign: 'right' }}>
          {currency(params.value)}
        </Typography>
      )
    },
    {
      field: "total",
      headerName: "Total",
      minWidth: 120,
      headerAlign: 'right',
      renderCell: (params) => {
        return <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32',mt:1.5,textAlign: 'right' }}>
          {currency(params.value)}
        </Typography>
      }
    },
    {
      field: "vendido_por",
      headerName: "Vendido por",
      minWidth: 180,
      headerAlign: 'right',
      align: 'right',
    },      
   
  ];

  // ---- Fetch Data Function ----
  const fetchData = async () => {
    setIsLoadingData(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/pos/sales?idcompany=${localStorage.getItem('idcompany')}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
          }
        }
      );

      if (!response.ok) {
        throw new Error('Error al cargar datos');
      }

      const data = await response.json();
      
      if (data.success) {
        const transformedData = data.sales.map((item, index) => ({
          id: item.id || `sale-${index}`,
          numero_comprobante: item.numero_comprobante || `SALE-${index}`,
          cliente_nombre: item.cliente_nombre || 'Consumidor Final',
          sucursal_nombre: item.sucursal_nombre || 'Sucursal no especificada',
          id_producto: item.id_producto,
          producto: item.producto_nombre || item.id_producto,
          cantidad_vendida: item.cantidad_vendida || 0,
          precio_unitario_vendido: parseFloat(item.precio_unitario_vendido) || 0,
          categoria_nombre: item.categoria_nombre || '',
          total: parseFloat(item.cantidad_vendida*item.precio_unitario_vendido) || 0,
          subtotal: parseFloat(item.cantidad_vendida*item.precio_unitario_vendido) || 0,
          impuestos: parseFloat(item.impuestos) || 0,
          estado_pago: item.estado_pago || 'pagado',
          metodo_pago: item.metodo_pago || 'efectivo',
          created_at: item.created_at || new Date().toISOString(),
          tipo_comprobante: item.tipo_comprobante || 'cf01',
          vendido_por: item.vendido_por || '',
        }));

        setRows(transformedData);
        setFilteredRows(transformedData);
        console.log('Sales data loaded successfully:', transformedData);
      } else {
        console.error('Error loading sales data:', data.message);
      }
    } catch (error) {
      console.error('Error fetching sales data:', error);
    } finally {
      setIsLoadingData(false);
    }
  };

  // ---- Filter Function ----
  const handleGenerate = () => {
    setIsLoading(true);
    
    // Simulate processing time
    setTimeout(() => {
      let filtered = [...rows];

      // Filter by branch
      if (branch) {
        filtered = filtered.filter(row => row.sucursal_nombre === branch);
      }

      // Filter by status
      if (status) {
        filtered = filtered.filter(row => row.estado_pago === status);
      }

      // Filter by category
      if (category) {
        filtered = filtered.filter(row => {
          return row.categoria_nombre === category;
        });
      }

      // Filter by date range
      if (dateFrom) {
        filtered = filtered.filter(row => {
          const rowDate = dayjs(row.created_at);
          return rowDate.isAfter(dateFrom) || rowDate.isSame(dateFrom, 'day');
        });
      }
      if (dateTo) {
        filtered = filtered.filter(row => {
          const rowDate = dayjs(row.created_at);
          return rowDate.isBefore(dateTo) || rowDate.isSame(dateTo, 'day');
        });
      }

      setFilteredRows(filtered);
      setIsLoading(false);
      console.log('Data filtered successfully:', filtered);
    }, 500);
  };

  const handleExportPDF = () => {
    console.log("Export PDF");
  };

  const handleExportExcel = () => {
    console.log("Export Excel");
  };

  const handlePrint = () => {
    console.log("Print");
  };

  const handleToggleCharts = () => {
    setShowCharts(!showCharts);
  };

  const handleClearFilters = () => {
    setBranch("");
    setStatus("");
    setCategory("");
    setDateFrom(null);
    setDateTo(null);
    setFilteredRows(rows); // Reset to show all data
  };

  // ---- Calculate Totals ----
  const totals = React.useMemo(() => {
    return filteredRows.reduce(
      (acc, r) => {
        acc.totalSales += parseFloat(r.total) || 0;
        acc.totalTransactions += 1;
        acc.paidSales += r.estado_pago === 'pagado' ? 1 : 0;
        acc.pendingSales += r.estado_pago === 'pendiente' ? 1 : 0;
        return acc;
      },
      { totalSales: 0, totalTransactions: 0, paidSales: 0, pendingSales: 0 }
    );
  }, [filteredRows]);

  // ---- Chart Data ----
  const chartData = React.useMemo(() => {
    const dailyData = {};
    
    filteredRows.forEach(row => {
      const date = dayjs(row.created_at).format('DD/MM');
      if (!dailyData[date]) {
        dailyData[date] = { date, sales: 0, transactions: 0 };
      }
      dailyData[date].sales += parseFloat(row.total) || 0;
      dailyData[date].transactions += 1;
    });

    return Object.values(dailyData).sort((a, b) => 
      dayjs(a.date, 'DD/MM').diff(dayjs(b.date, 'DD/MM'))
    );
  }, [filteredRows]);

  const paymentMethodData = React.useMemo(() => {
    const methods = {};
    
    filteredRows.forEach(row => {
      // Parse payment method from JSON if available
      try {
        const paymentData = typeof row.metodo_pago === 'string' 
          ? JSON.parse(row.metodo_pago) 
          : row.metodo_pago;
        
        if (Array.isArray(paymentData)) {
          paymentData.forEach(payment => {
            const method = payment.method || 'efectivo';
            if (!methods[method]) {
              methods[method] = 0;
            }
            methods[method] += parseFloat(payment.amount) || parseFloat(row.total) || 0;
          });
        } else {
          const method = paymentData?.method || 'efectivo';
          if (!methods[method]) {
            methods[method] = 0;
          }
          methods[method] += parseFloat(row.total) || 0;
        }
      } catch (e) {
        // Default to cash if parsing fails
        if (!methods['efectivo']) {
          methods['efectivo'] = 0;
        }
        methods['efectivo'] += parseFloat(row.total) || 0;
      }
    });

    return Object.entries(methods).map(([method, amount]) => ({
      name: method.charAt(0).toUpperCase() + method.slice(1),
      value: amount,
      color: method === 'efectivo' ? '#4caf50' : method === 'tarjeta' ? '#2196f3' : '#ff9800'
    }));
  }, [filteredRows]);

  // ---- Filter content ----
  const filterContent = (
    <Box>
      {/* Filter Controls Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left', mb: 1, ml:1 }}>
              Sucursal
            </Typography>
            <FormControl fullWidth>
              <Select
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                displayEmpty
                sx={{ backgroundColor: 'white', }}
              >
                <MenuItem value="">Todas las Sucursales</MenuItem>
                {filterOptions.branches.map((branchOption) => (
                  <MenuItem key={branchOption} value={branchOption}>
                    {branchOption}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>


        <Grid item size={{xs: 12, sm: 6, md: 2.4}}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left', mb: 1, ml:1 }}>
              Categoría
            </Typography>
            <FormControl fullWidth>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                displayEmpty
                sx={{ backgroundColor: 'white' }}
              >
                <MenuItem value="">Todas las Categorías</MenuItem>
                {filterOptions.categories.map((categoryOption) => (
                  <MenuItem key={categoryOption} value={categoryOption}>
                    {categoryOption}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>

        <Grid item size={{xs: 12, sm: 6, md: 2.4}}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left', mb: 1, ml:1 }}>
              Fecha Desde
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={dateFrom}
                onChange={(newValue) => setDateFrom(newValue)}
                label="Seleccionar fecha"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { backgroundColor: 'white' }
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        </Grid>

        <Grid item size={{xs: 12, sm: 6, md: 2.4}}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left', mb: 1, ml:1 }}>
              Fecha Hasta
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={dateTo}
                onChange={(newValue) => setDateTo(newValue)}
                label="Seleccionar fecha"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { backgroundColor: 'white' }
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        </Grid>

        <Grid item size={{xs: 12, sm: 6, md: 12}} display={"flex"} flexDirection={"row"} gap={2}>
          <Button 
            variant="contained" 
            onClick={handleGenerate} 
            color="primarybutton" 
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <FilterListIcon />}
            sx={{
              fontWeight: 600, 
              minWidth: 120, 
              mt: 3,
              background: 'linear-gradient(45deg, #18586b, #2a7a8a)',
              '&:hover': {
                background: 'linear-gradient(45deg, #0f3a42, #18586b)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(24, 88, 107, 0.3)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {isLoading ? 'Filtrando...' : 'Aplicar Filtros'}
          </Button>

          <Button 
            variant="outlined" 
            onClick={handleClearFilters} 
            disabled={isLoading}
            sx={{
              fontWeight: 600, 
              minWidth: 120, 
              mt: 3,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Limpiar Filtros
          </Button>
        </Grid>
      </Grid>

    </Box>
  );

  // ---- Load data on mount ----
  React.useEffect(() => {
    fetchData();
  }, []);

  return (
    <AppTheme themeComponents={xThemeComponents}>
      <CssBaseline />
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <SideMenu />
        <AppNavbar />
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            overflow: 'auto',
          })}
        >
          <Stack
            spacing={2}
            sx={{
              alignItems: 'center',
              mx: 3,
              pb: 5,
              mt: { xs: 8, md: 0 },
            }}
          >

          <Header parent={"Gestión Comercial"} child={"Ventas"} />
          <Container maxWidth="xl" sx={{ flexGrow: 1, py: 3 }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" px={2} mb={2}>
                <Typography variant="h4" fontWeight="bold">
                    Gestión de Ventas
                </Typography>                  
                <Button variant="contained" onClick={() => navigate('/gestion_comercial/ventas/cuadre')} color="primarybutton" sx={{fontWeight:"bold",
                  background: 'linear-gradient(45deg, #18586b, #2a7a8a)',
                  '&:hover': { background: 'linear-gradient(45deg, #0f3a42, #18586b)' }
                }}>
                  Cuadre de Caja
                </Button>
            </Box>

            {/* Metrics Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }} justifyContent="center">
              <Grid item size={{xs: 12, sm: 6, md: 3}}>
                <MetricCard 
                  label="Total Ventas" 
                  value={currency(totals.totalSales)} 
                  icon={<AttachMoneyIcon />}
                  color="#2e7d32"
                  trend="up"
                  trendValue="12"
                />
              </Grid>
              <Grid item size={{xs: 12, sm: 6, md: 3}}>
                <MetricCard 
                  label="Total Transacciones" 
                  value={totals.totalTransactions} 
                  icon={<ReceiptLongIcon />}
                  color="#1976d2"
                  trend="up"
                  trendValue="8"
                />
              </Grid>
         
            </Grid>
            {/* Filters */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              {filterContent}
            </Paper>



           

            {/* Data Table */}
            <Paper sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
              <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#18586b' }} textAlign="left">
                  Detalle de Ventas
                </Typography>
              </Box>
              <Box sx={{ height: 600, width: '100%',p:2 }}>
                <DataGrid
                  rows={filteredRows}
                  columns={columns}
                  minHeight={600}
                  disableRowSelectionOnClick
                  loading={isLoadingData}
                  sx={{
                    "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 },
                  }}

                  slots={{
                    noRowsOverlay: () => (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: '100%',
                        p: 3
                      }}>
                        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                          No hay datos de ventas
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                          Aplica filtros para ver las ventas o verifica que existan transacciones en el sistema
                        </Typography>
                        <Button 
                          variant="contained" 
                          onClick={handleGenerate}
                          sx={{ 
                            background: 'linear-gradient(45deg, #18586b, #2a7a8a)',
                            '&:hover': {
                              background: 'linear-gradient(45deg, #0f3a42, #18586b)',
                            }
                          }}
                        >
                          Aplicar Filtros
                        </Button>
                      </Box>
                    )
                  }}
                />
              </Box>
            </Paper>

            {/* Summary Footer */}
            {filteredRows.length > 0 && (
              <Paper sx={{ p: 3, mt: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#18586b' }} textAlign="left">
                  Resumen del Período
                </Typography>
                <Stack direction="row" spacing={4} flexWrap="wrap">
                  <Typography variant="body2">
                    <strong>Total Ventas:</strong> {currency(totals.totalSales)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total Transacciones:</strong> {totals.totalTransactions}
                  </Typography>
                  
                </Stack>
              </Paper>
            )}
          </Container>
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
